import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { useState, useRef, useEffect } from 'react';

export default function HeroSection() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setDropdownOpen(false);
  };

  return (
    <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Soft%20minimalist%20lifestyle%20composition%20with%20neatly%20arranged%20premium%20fashion%20items%20sneakers%20hoodies%20and%20accessories%20on%20a%20warm%20cream%20surface%20gentle%20natural%20light%20from%20large%20window%20editorial%20product%20photography%20sophisticated%20beige%20and%20taupe%20tones%20clean%20airy%20aesthetic%20calm%20organized%20atmosphere&width=1800&height=900&seq=hero-main-v3-01&orientation=landscape"
        alt="OOPBuy Spreadsheet Produkte"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>

      <div className="relative z-10 w-full px-4 md:px-6 py-16 md:py-20 flex flex-col items-center text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white max-w-4xl leading-tight">
          {t('hero.h1')}
        </h1>
        <p className="mt-4 text-base md:text-lg text-white/80 max-w-2xl font-heading italic">
          {t('hero.h1_sub')}
        </p>
        <p className="mt-4 text-sm md:text-base text-white/70 max-w-3xl">
          {t('hero.subtitle')}
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-3 mt-8">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-rocket-line"></i>
            {t('hero.cta_primary')}
          </a>
          <Link
            to="/oopbuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white font-semibold text-sm border border-white/30 hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-book-open-line"></i>
            {t('hero.cta_secondary')}
          </Link>
        </div>

        <div className="mt-10 w-full max-w-md relative" ref={dropdownRef}>
          <p className="text-white/60 text-xs mb-2 uppercase tracking-wider">{t('categories.title')}</p>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-5 py-3 rounded-xl bg-white/15 backdrop-blur-md border border-white/25 text-white text-sm font-medium hover:bg-white/20 transition-all cursor-pointer"
          >
            <span>{t('categories.select_placeholder')}</span>
            <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg transition-transform`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute mt-2 w-full max-w-md bg-white rounded-xl shadow-lg border border-background-200/70 overflow-hidden z-50 max-h-80 overflow-y-auto">
              {categories.map((cat) => (
                <button
                  key={cat.nameKey}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="w-full text-left px-5 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors flex items-center justify-between cursor-pointer border-b border-background-200/40 last:border-b-0 whitespace-nowrap"
                >
                  <span>{t(cat.nameKey)}</span>
                  <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}